import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PhoneFeature } from "services/phone-features.service";

export interface CallCenterAgent {
  id: string;
  name: string;
  extension: string;
  queue?: string[];
  level?: number[];
  position?: number[];
  state?: string;
  status?: string;
  agentDevices?: { networkIp: string; networkPort: string; userAgent: string }[];
  dndEnabled?: boolean;
  forwardBusyEnabled?: boolean;
  forwardNoAnswerEnabled?: boolean;
  forwardImmediateEnabled?: boolean;
}

interface AgentsState {
  items: CallCenterAgent[];
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: AgentsState = {
  items: [],
  status: "idle",
  error: null,
};

const agentsSlice = createSlice({
  name: "agents",
  initialState,
  reducers: {
    setAgents: (state, action: PayloadAction<CallCenterAgent[]>) => {
      state.items = action.payload.map(agent => {
        const normalizedId = agent.id?.trim().toLowerCase() || '';
        const name = agent.name?.trim();

        return {
          ...agent,
          id: normalizedId,
          name: name && name.length > 0 ? name : `Agent (${agent.extension || 'N/A'})`,
          extension: agent.extension?.trim() || '—',
          queue: agent.queue || [],
          level: agent.level || [],
          position: agent.position || [],
          status: agent.status?.trim() || undefined,
          state: agent.state?.trim() || undefined,
        };
      });
      state.status = "succeeded";
      state.error = null;
    },


    updateAgentStatus: (
      state,
      action: PayloadAction<{ id?: string; status?: string; state?: string }>
    ) => {
      const rawId = action.payload.id;
      if (!rawId) {
        console.warn("❌ Missing agent ID in updateAgentStatus:", action.payload);
        return;
      }

      const idToMatch = rawId.trim().toLowerCase();
      const agent = state.items.find(a => a.id === idToMatch);

      if (agent) {
        if (action.payload.status !== undefined) agent.status = action.payload.status?.trim() || undefined;
        if (action.payload.state !== undefined) agent.state = action.payload.state?.trim() || undefined;
      } else {
        console.warn("❌ Agent not found for update:", idToMatch);
      }
    },

    updateAgentTiers: (
      state,
      action: PayloadAction<{ id: string; queue: string; tier: number; position: number; tierState: string }>
    ) => {
      const { id, queue, tier, position, tierState } = action.payload;
      const idToMatch = id.trim().toLowerCase();
      const agent = state.items.find(a => a.id === idToMatch);

      if (agent) {
        if (!agent.queue) agent.queue = [];
        if (!agent.level) agent.level = [];
        if (!agent.position) agent.position = [];

        const existingQueueIndex = agent.queue.findIndex(q => q === queue);

        if (existingQueueIndex >= 0) {
          // Update existing queue's level and position
          agent.level[existingQueueIndex] = tier;
          agent.position[existingQueueIndex] = position;
        } else {
          // Add new queue with its level and position
          agent.queue.push(queue);
          agent.level.push(tier);
          agent.position.push(position);
        }
      } else {
        console.warn("❌ Agent not found for tier update:", idToMatch);
      }
    },

    updateAgentsFromActiveData: (
      state,
      action: PayloadAction<{ queues: any[]; agents: any[] }>
    ) => {
      const { queues, agents } = action.payload;

      // Reset all agents' queue data
      state.items.forEach(agent => {
        agent.queue = [];
        agent.level = [];
        agent.position = [];
      });

      // Update agent status and state
      agents.forEach((agentStatus: any) => {
        const agentId = agentStatus.name.trim().toLowerCase();
        const agent = state.items.find(a => a.id === agentId);

        if (agent) {
          agent.status = agentStatus.status;
          agent.state = agentStatus.state;
        }
      });

      // Update agent queue assignments from queue data
      queues.forEach((queue: any) => {
        queue.tiers?.forEach((tier: any) => {
          const agentId = tier.agent.trim().toLowerCase();
          const agent = state.items.find(a => a.id === agentId);

          if (agent) {
            if (!agent.queue) agent.queue = [];
            if (!agent.level) agent.level = [];
            if (!agent.position) agent.position = [];

            // Add queue, level, and position to the arrays
            agent.queue.push(queue.name);
            agent.level.push(parseInt(tier.level));
            agent.position.push(parseInt(tier.position));
          }
        });
      });
    },


    updateAgentStateByName: (state, action: PayloadAction<{ agentId: string; state: string }>) => {
      const state2 = {
        ...state,
        items: JSON.parse(JSON.stringify(state.items)),
      };
      const agentIndex = state2.items.findIndex(
        (agent:any) => agent.id === action.payload.agentId
      );
      if (agentIndex !== -1) {
        state2.items[agentIndex].state = action.payload.state;
        state.items = state2.items;
      }
    },

    setAgentsLoading: (state) => {
      state.status = "loading";
      state.error = null;
    },
    setAgentsError: (state, action: PayloadAction<string>) => {
      state.status = "failed";
      state.error = action.payload;
    },
    resetAgents: (state) => {
      state.items = [];
      state.status = "idle";
      state.error = null;
    },
    updateAgentsDevices: (state, action: PayloadAction<{ agents: { agent: string; agentDevices: { networkIp: string; networkPort: string; userAgent: string }[] }[] }>) => {
      action.payload.agents.forEach((agentData: any) => {
        const agentIndex = state.items.findIndex(agent => agent.extension === agentData.agent);
        if (agentIndex !== -1) {
          state.items[agentIndex] = {
            ...state.items[agentIndex],
            agentDevices: agentData.agentDevices,
          };
        }
      });
    },
    addAgentDevice: (state, action: PayloadAction<{ agent: string; deviceInfo: { networkIp: string; networkPort: string; userAgent: string } }>) => {
      const { agent, deviceInfo } = action.payload;
      const agentIndex = state.items.findIndex(a => a.extension === agent);
      
      if (agentIndex !== -1) {
        const currentAgent = state.items[agentIndex];
        const existingDevices = currentAgent.agentDevices || [];
        
        const deviceExists = existingDevices.some(device => 
          device.networkIp === deviceInfo.networkIp && 
          device.networkPort === deviceInfo.networkPort
        );
        
        if (!deviceExists) {
          state.items[agentIndex] = {
            ...currentAgent,
            agentDevices: [...existingDevices, deviceInfo],
            status: currentAgent.status === 'Logged Out' ? 'Available' : currentAgent.status,
          };
        } else {
          if (currentAgent.status === 'Logged Out') {
            state.items[agentIndex] = {
              ...currentAgent,
              status: 'Available',
            };
          }
        }
      }
    },
    removeAgentDevice: (state, action: PayloadAction<{ agent: string; deviceInfo: { networkIp?: string; networkPort?: string; userAgent?: string } }>) => {
      const { agent, deviceInfo } = action.payload;
      const agentIndex = state.items.findIndex(a => a.extension === agent);
      
      if (agentIndex !== -1) {
        const currentAgent = state.items[agentIndex];
        
        if (currentAgent.agentDevices && currentAgent.agentDevices.length > 0) {
          const updatedDevices = currentAgent.agentDevices.filter(device => {
            const ipMatch = deviceInfo.networkIp ? device.networkIp !== deviceInfo.networkIp : true;
            const portMatch = deviceInfo.networkPort ? device.networkPort !== deviceInfo.networkPort : true;
            const agentMatch = deviceInfo.userAgent ? device.userAgent !== deviceInfo.userAgent : true;
            
            return ipMatch || portMatch || agentMatch;
          });
          
          state.items[agentIndex] = {
            ...currentAgent,
            agentDevices: updatedDevices,
            status: updatedDevices.length === 0 ? 'Logged Out' : currentAgent.status,
          };
        } else {
          state.items[agentIndex] = {
            ...currentAgent,
            status: 'Logged Out',
          };
        }
      }
    },
    updateAgentsPhoneFeatures: (state, action: PayloadAction<PhoneFeature[]>) => {
      action.payload.forEach((phoneFeature) => {
        const agentExtension = phoneFeature.user.split("@")[0];
        const agentIndex = state.items.findIndex(agent => agent.extension === agentExtension);
        
        if (agentIndex !== -1) {
          
          const updates = {
            ...(typeof phoneFeature.dndEnabled === "boolean" && {
              dndEnabled: phoneFeature.dndEnabled,
            }),
            ...(typeof phoneFeature.forwardBusyEnabled === "boolean" && {
              forwardBusyEnabled: phoneFeature.forwardBusyEnabled,
            }),
            ...(typeof phoneFeature.forwardNoAnswerEnabled === "boolean" && {
              forwardNoAnswerEnabled: phoneFeature.forwardNoAnswerEnabled,
            }),
            ...(typeof phoneFeature.forwardImmediateEnabled === "boolean" && {
              forwardImmediateEnabled: phoneFeature.forwardImmediateEnabled,
            }),
          };
    
          state.items[agentIndex] = {
            ...state.items[agentIndex],
            ...updates,
          };
        }
      });
    },
  },
});

export const {
  setAgents,
  updateAgentStatus,
  updateAgentTiers,
  updateAgentsFromActiveData,
  updateAgentStateByName,
  setAgentsLoading,
  setAgentsError,
  resetAgents,
  updateAgentsDevices,
  addAgentDevice,
  removeAgentDevice,
  updateAgentsPhoneFeatures,
} = agentsSlice.actions;

export default agentsSlice.reducer;
